export interface SeoFaq {
  q: string;
  a: string;
}

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  /** Service name → emits Service JSON-LD when present. */
  service?: string;
  /** FAQ pairs → emits FAQPage JSON-LD when present. */
  faqs?: SeoFaq[];
}

/** Default tags used for any public page without a specific entry. */
export const DEFAULT_SEO: SeoMeta = {
  title: 'Online CA & Legal Services in India | LegalStack',
  description:
    'File ITR & GST, register your company online with verified CAs & lawyers. Transparent pricing, trusted by 1L+ clients. Get started today!',
  keywords:
    'online legal services india, ca services online, gst registration, itr filing, company registration, legal compliance, LegalStack',
};

/**
 * SEO meta for public (no-login) pages, keyed by the route path (no leading slash).
 * Titles are kept ≤ 60 chars and descriptions ≤ 155 chars for SERP display.
 */
export const SEO_CONFIG: Record<string, SeoMeta> = {
  '': {
    title: 'Online CA & Legal Services in India | LegalStack',
    description:
      'File ITR & GST, register your company online with verified CAs & lawyers. Transparent pricing, 1L+ happy clients. Get started today!',
    keywords:
      'online legal services india, gst registration online, itr filing online, company registration india, legal compliance services, ca online',
  },

  'home-page-new': {
    title: 'Online CA & Legal Services in India | LegalStack',
    description:
      'ITR & GST filing, company & trademark registration online with verified CAs and lawyers. Transparent pricing, trusted by 1L+ clients.',
    keywords:
      'online legal services india, gst registration, itr filing, company registration, trademark registration, legal compliance',
  },

  'itr-filling': {
    title: 'ITR Filing Online from ₹999 | Expert CA | LegalStack',
    description:
      'File your Income Tax Return online with expert CAs. Maximum refund, e-verification & 100% accuracy. Trusted by 1L+ filers. Start now!',
    keywords:
      'itr filing online, income tax return filing, file itr online india, ca for itr filing, tax filing online, itr filing for freelancers',
    service: 'ITR Filing',
    faqs: [
      { q: 'What is the cost of ITR filing on LegalStack?', a: 'Online ITR filing for individuals starts at just ₹999 and includes expert CA review, e-verification and refund tracking.' },
      { q: 'What documents are required to file ITR online?', a: 'You typically need your PAN, Aadhaar, Form 16, bank statements, and details of any other income, investments or deductions. Our CA guides you through the exact list.' },
      { q: 'Can I get a maximum refund with LegalStack?', a: 'Yes. Our experts review all eligible deductions and exemptions to legally maximise your income tax refund.' },
      { q: 'How long does ITR filing take?', a: 'Most returns are prepared and filed within 24–48 hours after you share your documents.' },
    ],
  },

  'gst': {
    title: 'GST Registration & Return Filing Online | LegalStack',
    description:
      'Get GST registration online in 24 hrs and file GST returns on time with expert CAs. Lowest fees, 100% online, free consultation. Start now!',
    keywords:
      'gst registration online, gst return filing, gstr-1 gstr-3b filing, new gst registration, gst number online, gst services india',
    service: 'GST Registration & Return Filing',
    faqs: [
      { q: 'How long does GST registration take?', a: 'With LegalStack, your GST application is filed the same day and the GSTIN is usually issued within 24–72 hours, subject to government processing.' },
      { q: 'What documents are needed for GST registration?', a: 'PAN, Aadhaar, business address proof, bank details and a photograph of the proprietor/partners/directors are generally required.' },
      { q: 'Who needs GST registration?', a: 'Businesses with turnover above the threshold limit, inter-state suppliers, e-commerce sellers and those wanting input tax credit need GST registration.' },
      { q: 'Can LegalStack file my monthly GST returns?', a: 'Yes. We offer affordable monthly and quarterly GST return filing plans handled by expert CAs so you never miss a deadline.' },
    ],
  },

  'tds-calculator': {
    title: 'TDS Calculator Online (FY 2024-25) | LegalStack',
    description:
      'Calculate TDS online instantly and accurately for salary, rent & payments. Free, easy & updated for latest rates. Try LegalStack now!',
    keywords:
      'tds calculator, tds calculator online, tds on salary calculator, tds deduction calculator, income tax tds calculator india',
    faqs: [
      { q: 'Is the LegalStack TDS calculator free to use?', a: 'Yes, the TDS calculator is completely free and gives instant, accurate results for the latest financial year.' },
      { q: 'What can I calculate with the TDS calculator?', a: 'You can estimate TDS deductions on salary, rent, professional fees, interest and other payments as per current income tax rates.' },
    ],
  },

  'hra-calculator': {
    title: 'HRA Calculator Online — Save Tax | LegalStack',
    description:
      'Calculate your HRA exemption online and maximise tax savings in seconds. Free, accurate & easy to use. Try the LegalStack HRA calculator!',
    keywords:
      'hra calculator, hra exemption calculator, house rent allowance calculator, hra tax calculator online india',
    faqs: [
      { q: 'How is HRA exemption calculated?', a: 'HRA exemption is the least of: actual HRA received, 50%/40% of salary (metro/non-metro), or rent paid minus 10% of salary. Our calculator computes it instantly.' },
      { q: 'Is the HRA calculator free?', a: 'Yes, the LegalStack HRA calculator is free and helps you find your exact tax-exempt HRA in seconds.' },
    ],
  },

  'ctc-calculator': {
    title: 'CTC to In-Hand Salary Calculator | LegalStack',
    description:
      'Convert CTC to in-hand salary online instantly. See take-home pay, deductions & taxes. Free & accurate. Try the LegalStack calculator!',
    keywords:
      'ctc calculator, ctc to in hand salary calculator, take home salary calculator india, salary breakup calculator',
    faqs: [
      { q: 'How do I calculate in-hand salary from CTC?', a: 'In-hand salary is CTC minus deductions like PF, professional tax and income tax (TDS). Our calculator breaks this down for you instantly.' },
      { q: 'Is the CTC calculator accurate?', a: 'Yes, it gives a close estimate of your take-home pay based on standard salary components and current tax rules.' },
    ],
  },

  'new-tax-regime-and-old-tax-regime': {
    title: 'Old vs New Tax Regime Calculator | LegalStack',
    description:
      'Compare old vs new tax regime online and find which saves you more tax. Free, instant & accurate for FY 2024-25. Calculate with LegalStack!',
    keywords:
      'old vs new tax regime, tax regime calculator, new tax regime calculator, income tax regime comparison india',
  },

  'import-export-code': {
    title: 'IEC (Import Export Code) Registration | LegalStack',
    description:
      'Get your Import Export Code (IEC) online in 1–2 days with expert help. 100% online, lowest fees, lifetime validity. Apply with LegalStack!',
    keywords:
      'iec code registration, import export code online, iec registration india, dgft iec code, import export license',
    service: 'Import Export Code (IEC) Registration',
    faqs: [
      { q: 'How long is an IEC valid?', a: 'An Import Export Code has lifetime validity — there is no need to renew it once issued.' },
      { q: 'Who needs an IEC code?', a: 'Any person or business importing goods into or exporting goods/services out of India needs an IEC.' },
      { q: 'How quickly can I get my IEC?', a: 'With LegalStack, the IEC is typically issued within 1–2 working days after document submission.' },
    ],
  },

  'private-limited-company-registration': {
    title: 'Private Limited Company Registration | LegalStack',
    description:
      'Register your Pvt Ltd company online in 7 days — DIN, DSC, PAN, TAN & MoA included. Expert support, transparent pricing. Start today!',
    keywords:
      'private limited company registration, pvt ltd registration online, company registration india, register a company, incorporate company',
    service: 'Private Limited Company Registration',
    faqs: [
      { q: 'How long does it take to register a Private Limited Company?', a: 'Registration is typically completed within 7–10 working days, depending on document readiness and MCA processing times.' },
      { q: "What's included in LegalStack's Pvt Ltd registration?", a: 'It includes DSC, DIN, name approval, MoA & AoA drafting, Certificate of Incorporation, PAN and TAN.' },
      { q: 'How many directors are required for a Pvt Ltd company?', a: 'A minimum of two directors and two shareholders are required; the same persons can be both directors and shareholders.' },
      { q: 'Is there a minimum capital requirement?', a: 'No. There is no mandatory minimum paid-up capital to register a Private Limited Company in India.' },
    ],
  },

  'partnership-firm': {
    title: 'Partnership Firm Registration Online | LegalStack',
    description:
      'Register your partnership firm online with expert help — deed drafting, PAN & registration included. Fast, affordable. Get a free quote!',
    keywords:
      'partnership firm registration, partnership deed registration, register partnership firm online india, partnership firm pan',
    service: 'Partnership Firm Registration',
    faqs: [
      { q: 'Is registration of a partnership firm mandatory?', a: 'Registration is optional but highly recommended, as a registered firm can enforce its rights in court and open a current account more easily.' },
      { q: "What's included in partnership firm registration?", a: 'It includes partnership deed drafting, PAN application and assistance with firm registration with the Registrar of Firms.' },
      { q: 'How many partners are required?', a: 'A minimum of two partners is required to form a partnership firm in India.' },
    ],
  },

  'talk-to-advisor': {
    title: 'Talk to a CA / Lawyer Online | LegalStack',
    description:
      'Consult verified CAs & lawyers online for tax, GST, company & legal advice. Affordable, confidential & instant. Book your consultation now!',
    keywords:
      'talk to ca online, consult lawyer online, online tax consultation, legal advice online india, ca consultation',
  },

  'chat-with-consultation': {
    title: 'Online CA & Legal Consultation Chat | LegalStack',
    description:
      'Chat with expert CAs & lawyers online for instant tax, GST & legal help. Confidential, affordable & quick. Start your consultation now!',
    keywords:
      'online ca consultation, legal consultation chat, talk to lawyer online, tax advice online india',
  },

  'service': {
    title: 'Business, Tax & Legal Services Online | LegalStack',
    description:
      'Explore ITR, GST, company registration, trademark & compliance services online with verified experts. Transparent pricing. Get started!',
    keywords:
      'online legal services, tax services online, business registration services, compliance services india, ca services',
  },

  'become-an-advisor': {
    title: 'Become a CA / Lawyer Partner | LegalStack',
    description:
      'Join LegalStack as a verified CA or lawyer and grow your practice online. Get clients, flexible work & timely payouts. Apply today!',
    keywords:
      'become a ca partner, lawyer partner program, ca jobs online, legal advisor partner india',
  },

  'partner-user': {
    title: 'Partner with LegalStack | Grow Your Business',
    description:
      'Partner with LegalStack and earn by referring tax & legal services. Easy onboarding, attractive payouts & full support. Join now!',
    keywords:
      'legalstack partner, referral partner program, business partner india, earn with legalstack',
  },

  'support-chat': {
    title: 'Customer Support — Tax & Legal Help | LegalStack',
    description:
      'Get quick help with your ITR, GST, company registration & orders. Chat with LegalStack support for fast, friendly assistance. Start now!',
    keywords:
      'legalstack support, customer support tax services, gst help, itr filing support india',
  },

  'about-us': {
    title: 'About LegalStack — Online CA & Legal Experts',
    description:
      "India's trusted platform for online tax, GST & legal services. Verified CAs & lawyers, transparent pricing, 1L+ clients. Know more!",
    keywords:
      'about legalstack, online legal services company, ca firm online india, tax & legal experts, legalstack company profile',
    faqs: [
      { q: 'What services does LegalStack offer?', a: 'LegalStack offers end-to-end online legal and CA services including ITR filing, GST registration & returns, company and LLP registration, trademark filing, ROC compliance, legal documentation and expert financial consulting.' },
      { q: 'Is LegalStack a trusted platform for legal and CA services?', a: 'Yes. LegalStack is trusted by over 1,00,000 clients across India and works only with qualified, verified Chartered Accountants and lawyers, with transparent pricing and secure digital processing.' },
      { q: 'Are the CAs and lawyers on LegalStack verified?', a: 'Every Chartered Accountant, Company Secretary and lawyer on LegalStack is qualified and verified before onboarding, so your compliance is always handled by certified professionals.' },
      { q: 'Is LegalStack available across India?', a: 'Yes. LegalStack is a 100% online platform with PAN-India coverage, so you can access our legal and CA services from anywhere in the country.' },
      { q: 'How is LegalStack pricing structured?', a: 'LegalStack follows transparent, affordable pricing with no hidden charges. You see the complete price upfront before you pay.' },
    ],
  },

  'contact-us': {
    title: 'Contact LegalStack | Tax & Legal Support',
    description:
      'Get in touch with LegalStack for ITR, GST, company registration & legal queries. Call, email or chat with our experts today!',
    keywords:
      'contact legalstack, tax services contact, gst registration help, legal services contact india',
  },

  'how-it-works': {
    title: 'How LegalStack Works | Simple 3-Step Process',
    description:
      'See how LegalStack makes ITR, GST & company registration easy in 3 steps — choose, pay & let verified experts handle it. Get started!',
    keywords:
      'how legalstack works, online tax filing process, company registration process, gst registration steps india',
  },

  'faq': {
    title: 'FAQs — ITR, GST & Company Registration | LegalStack',
    description:
      'Answers to common questions on ITR filing, GST registration, company setup & compliance. Get clarity & start with LegalStack today!',
    keywords:
      'itr filing faq, gst registration faq, company registration questions, legal services faq india',
  },

  'news': {
    title: 'Tax & Compliance News & Updates | LegalStack',
    description:
      'Stay updated with the latest tax, GST, company law & compliance news for Indian businesses. Read expert insights on LegalStack.',
    keywords:
      'tax news india, gst updates, compliance news, income tax updates, business law news',
  },

  'press-and-media': {
    title: 'Press & Media | LegalStack in the News',
    description:
      'LegalStack in the news — press releases, media coverage & announcements about our online tax and legal services. Read the latest!',
    keywords:
      'legalstack press, media coverage, legalstack news, company announcements',
  },

  'pricing-return-policy': {
    title: 'Pricing & Refund Policy | LegalStack',
    description:
      'Transparent pricing and clear refund policy for LegalStack tax & legal services. No hidden fees. Learn more before you get started!',
    keywords:
      'legalstack pricing, refund policy, tax service charges, legal services pricing india',
  },

  'terms': {
    title: 'Terms & Conditions | LegalStack',
    description:
      'Read the terms & conditions for using LegalStack online tax, GST and legal services. Understand your rights before you begin.',
    keywords: 'legalstack terms and conditions, terms of service, user agreement',
  },

  'privacy': {
    title: 'Privacy Policy | LegalStack',
    description:
      'Learn how LegalStack protects your data and privacy while delivering online tax & legal services. Read our privacy policy here.',
    keywords: 'legalstack privacy policy, data protection, privacy terms india',
  },

  'refund': {
    title: 'Refund Policy | LegalStack',
    description:
      'Read the LegalStack refund policy for tax & legal services. Clear, fair & transparent — know your refund rights before you start.',
    keywords: 'legalstack refund policy, money back policy, refund terms',
  },

  'shipping-policy': {
    title: 'Shipping & Delivery Policy | LegalStack',
    description:
      'Read the LegalStack delivery policy for documents and service completion timelines. Transparent and reliable. Learn more here.',
    keywords: 'legalstack shipping policy, delivery policy, service delivery terms',
  },
};
